import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FaInfoCircle, FaMapMarkerAlt } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import useDebounce from "../hooks/useDebounce";
import { loadGoogleMapsPlaces } from "../utils/googleMapsLoader";
import {
  extractAddressFields,
  extractMunicipalityCandidates,
  resolveMunicipalityMatch,
  resolveMunicipalityMatchByCoordinates,
} from "../utils/municipalityMatcher";

const MIN_QUERY_LENGTH = 3;
const SWEDEN_CENTER = { lat: 62.3875, lng: 16.3255 };

function formatGoogleError(error) {
  if (!error?.message) {
    return "Adresssökningen kunde inte startas just nu.";
  }

  if (error.message.includes("VITE_GOOGLE_MAPS_API_KEY")) {
    return "Adresssökningen är inte tillgänglig utan Google Maps API-nyckel.";
  }

  return "Adresssökningen kunde inte startas just nu.";
}

function getPrefillPatch(addressFields = {}) {
  return Object.entries(addressFields).reduce((patch, [key, value]) => {
    if (value) {
      patch[key] = value;
    }

    return patch;
  }, {});
}

function applyLookupSelection(
  { municipalityMatch, addressFields },
  setText,
  setRemValKommun
) {
  const nextPatch = getPrefillPatch(addressFields);

  if (municipalityMatch) {
    setRemValKommun(municipalityMatch.object);
    nextPatch.kobject = municipalityMatch.object;
    nextPatch.kommun = municipalityMatch.municipality;
  }

  if (Object.keys(nextPatch).length > 0) {
    setText((prev) => ({
      ...prev,
      ...nextPatch,
    }));
  }
}

function getLookupMessage({ municipalityMatch, addressFields }) {
  const hasAddressPrefill = Object.values(addressFields || {}).some(Boolean);

  if (municipalityMatch && hasAddressPrefill) {
    return `Adressuppgifterna fylldes i och kommun satt till ${municipalityMatch.municipality}.`;
  }

  if (municipalityMatch) {
    return `Kommun satt till ${municipalityMatch.municipality}.`;
  }

  if (hasAddressPrefill) {
    return "Adressuppgifterna fylldes i. Välj kommun manuellt om det behövs.";
  }

  return "Adressen hittades men kunde inte användas för att fylla i formuläret automatiskt.";
}

function formatCoordinateSelectionLabel(location) {
  const lat =
    typeof location?.lat === "function" ? location.lat() : location?.lat;
  const lng =
    typeof location?.lng === "function" ? location.lng() : location?.lng;

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return "Vald plats på karta";
  }

  return `Vald plats (${lat.toFixed(5)}, ${lng.toFixed(5)})`;
}

const AddressLookup = ({ ddKommun, setText, setRemValKommun }) => {
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [lookupMessage, setLookupMessage] = useState("");
  const [isLoadingPredictions, setIsLoadingPredictions] = useState(false);
  const [isResolvingSelection, setIsResolvingSelection] = useState(false);
  const [googleError, setGoogleError] = useState("");
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [mapSelection, setMapSelection] = useState(null);
  const [isResolvingMapSelection, setIsResolvingMapSelection] = useState(false);
  const debouncedQuery = useDebounce(query.trim(), 350);

  const autocompleteSuggestionRef = useRef(null);
  const autocompleteSessionTokenCtorRef = useRef(null);
  const geocoderRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const mapMarkerRef = useRef(null);
  const mapClickListenerRef = useRef(null);
  const latestRequestIdRef = useRef(0);
  const skipNextPredictionFetchRef = useRef(false);

  useEffect(() => {
    let isCancelled = false;

    async function setupGoogleServices() {
      try {
        const maps = await loadGoogleMapsPlaces();
        let placesLibrary = null;

        if (typeof maps.importLibrary === "function") {
          placesLibrary = await maps.importLibrary("places");
        }

        if (!window.google?.maps?.places) {
          throw new Error("Google Maps Places library is not available.");
        }

        if (isCancelled) {
          return;
        }

        autocompleteSuggestionRef.current =
          placesLibrary?.AutocompleteSuggestion ||
          window.google.maps.places.AutocompleteSuggestion;
        autocompleteSessionTokenCtorRef.current =
          placesLibrary?.AutocompleteSessionToken ||
          window.google.maps.places.AutocompleteSessionToken;
        geocoderRef.current = new window.google.maps.Geocoder();

        if (!autocompleteSuggestionRef.current) {
          throw new Error(
            "Google Maps autocomplete suggestions are not available."
          );
        }

        if (!autocompleteSessionTokenCtorRef.current) {
          throw new Error(
            "Google Maps autocomplete session tokens are not available."
          );
        }

        sessionTokenRef.current = new autocompleteSessionTokenCtorRef.current();
      } catch (error) {
        if (!isCancelled) {
          setGoogleError(formatGoogleError(error));
        }
      }
    }

    setupGoogleServices();

    return () => {
      isCancelled = true;

      if (mapClickListenerRef.current) {
        window.google?.maps?.event?.removeListener(mapClickListenerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const className = "addressLookupModalOpen";

    if (isMapOpen) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }

    return () => {
      document.body.classList.remove(className);
    };
  }, [isMapOpen]);

  useEffect(() => {
    if (!autocompleteSuggestionRef.current || !sessionTokenRef.current) {
      return;
    }

    if (skipNextPredictionFetchRef.current) {
      skipNextPredictionFetchRef.current = false;
      setPredictions([]);
      setIsLoadingPredictions(false);
      return;
    }

    if (!debouncedQuery) {
      setPredictions([]);
      setIsLoadingPredictions(false);
      return;
    }

    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setPredictions([]);
      setIsLoadingPredictions(false);
      return;
    }

    let isCancelled = false;
    const requestId = ++latestRequestIdRef.current;
    setIsLoadingPredictions(true);
    setLookupMessage("");

    async function fetchPredictions() {
      try {
        const { suggestions = [] } =
          await autocompleteSuggestionRef.current.fetchAutocompleteSuggestions({
            includedRegionCodes: ["se"],
            input: debouncedQuery,
            language: "sv",
            region: "se",
            sessionToken: sessionTokenRef.current,
          });

        if (isCancelled || requestId !== latestRequestIdRef.current) {
          return;
        }

        setPredictions(
          suggestions
            .map((suggestion) => suggestion.placePrediction)
            .filter(Boolean)
        );
      } catch {
        if (isCancelled || requestId !== latestRequestIdRef.current) {
          return;
        }

        setPredictions([]);
        setLookupMessage("Adresssökningen returnerade inget användbart svar.");
      } finally {
        if (!isCancelled && requestId === latestRequestIdRef.current) {
          setIsLoadingPredictions(false);
        }
      }
    }

    fetchPredictions();

    return () => {
      isCancelled = true;
    };
  }, [debouncedQuery]);

  useEffect(() => {
    if (!isMapOpen || !mapContainerRef.current || !window.google?.maps) {
      return;
    }

    const mapContainer = mapContainerRef.current;
    const activeMapContainer = mapRef.current?.getDiv?.();

    if (mapRef.current && activeMapContainer !== mapContainer) {
      if (mapClickListenerRef.current) {
        window.google.maps.event.removeListener(mapClickListenerRef.current);
        mapClickListenerRef.current = null;
      }

      mapMarkerRef.current?.setMap(null);
      mapMarkerRef.current = null;
      mapRef.current = null;
    }

    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(mapContainer, {
        center: SWEDEN_CENTER,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        zoom: 5,
      });

      mapMarkerRef.current = new window.google.maps.Marker({
        map: null,
      });

      mapClickListenerRef.current = mapRef.current.addListener(
        "click",
        async (event) => {
          if (!event.latLng) {
            return;
          }

          setIsResolvingMapSelection(true);
          setLookupMessage("");

          const selection = await reverseGeocodeLocation(event.latLng);

          setIsResolvingMapSelection(false);

          if (!selection) {
            setMapSelection(null);
            setLookupMessage(
              "Platsen valdes på kartan men ingen adress kunde hämtas."
            );
            return;
          }

          mapMarkerRef.current.setMap(mapRef.current);
          mapMarkerRef.current.setPosition(event.latLng);
          mapRef.current.panTo(event.latLng);
          mapRef.current.setZoom(16);
          setMapSelection(selection);
        }
      );
    }

    window.google.maps.event.trigger(mapRef.current, "resize");
  }, [isMapOpen]);

  function geocodeLocation(location) {
    return new Promise((resolve, reject) => {
      geocoderRef.current.geocode({ location }, (results, status) => {
        if (status === "OK") {
          resolve(results || []);
          return;
        }

        reject(new Error(status || "GEOCODER_FAILED"));
      });
    });
  }

  async function reverseGeocodeLocation(latLng) {
    const municipalityMatchByCoordinates =
      resolveMunicipalityMatchByCoordinates(latLng, ddKommun);

    if (!geocoderRef.current) {
      if (!municipalityMatchByCoordinates) {
        return null;
      }

      return {
        addressFields: {},
        formattedAddress: formatCoordinateSelectionLabel(latLng),
        location: latLng,
        municipalityMatch: municipalityMatchByCoordinates,
      };
    }

    try {
      const results = await geocodeLocation(latLng);
      const result = results.find((candidate) =>
        candidate.address_components?.some(
          (component) =>
            component.types?.includes("country") &&
            (component.short_name === "SE" || component.shortText === "SE")
        )
      );

      if (!result) {
        if (!municipalityMatchByCoordinates) {
          return null;
        }

        return {
          addressFields: {},
          formattedAddress: formatCoordinateSelectionLabel(latLng),
          location: latLng,
          municipalityMatch: municipalityMatchByCoordinates,
        };
      }

      const municipalityMatch =
        municipalityMatchByCoordinates ||
        resolveMunicipalityMatch(
          extractMunicipalityCandidates(result.address_components),
          ddKommun
        );
      const addressFields = extractAddressFields(result.address_components);

      return {
        addressFields,
        formattedAddress: result.formatted_address,
        location: latLng,
        municipalityMatch,
      };
    } catch {
      if (!municipalityMatchByCoordinates) {
        return null;
      }

      return {
        addressFields: {},
        formattedAddress: formatCoordinateSelectionLabel(latLng),
        location: latLng,
        municipalityMatch: municipalityMatchByCoordinates,
      };
    }
  }

  async function handlePredictionSelect(prediction) {
    if (!prediction?.toPlace) {
      return;
    }

    setIsResolvingSelection(true);
    setPredictions([]);
    setLookupMessage("");
    skipNextPredictionFetchRef.current = true;
    setQuery(prediction.text?.toString?.() || query);

    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: [
          "addressComponents",
          "displayName",
          "formattedAddress",
          "location",
          "viewport",
        ],
      });

      const municipalityMatch =
        resolveMunicipalityMatchByCoordinates(place.location, ddKommun) ||
        resolveMunicipalityMatch(
          extractMunicipalityCandidates(place.addressComponents),
          ddKommun
        );
      const addressFields = extractAddressFields(place.addressComponents);

      applyLookupSelection(
        { municipalityMatch, addressFields },
        setText,
        setRemValKommun
      );
      setLookupMessage(getLookupMessage({ municipalityMatch, addressFields }));

      if (place.location && mapRef.current) {
        if (place.viewport) {
          mapRef.current.fitBounds(place.viewport);
        } else {
          mapRef.current.panTo(place.location);
          mapRef.current.setZoom(16);
        }

        mapMarkerRef.current?.setMap(mapRef.current);
        mapMarkerRef.current?.setPosition(place.location);
      }
    } catch {
      setLookupMessage("Adressen kunde inte läsas in fullt ut. Försök igen.");
    } finally {
      sessionTokenRef.current = new autocompleteSessionTokenCtorRef.current();
      setIsResolvingSelection(false);
    }
  }

  function handleMapSelectionConfirm() {
    if (!mapSelection) {
      return;
    }

    skipNextPredictionFetchRef.current = true;
    setQuery(mapSelection.formattedAddress);
    setPredictions([]);

    applyLookupSelection(mapSelection, setText, setRemValKommun);
    setLookupMessage(getLookupMessage(mapSelection));

    setIsMapOpen(false);
  }

  return (
    <>
      <h6 className="txtH6 addressLookupHeader">
        Adress / plats{" "}
        <FaInfoCircle
          id="info-circel-address-lookup"
          className="infoCircelTop"
          data-tooltip-html="Skriv minst 3 tecken för att söka efter en adress i Sverige. Vald adress försöker automatiskt sätta Kommun."
        />
        <Tooltip
          anchorId="info-circel-address-lookup"
          className="infoCircelTop2"
          multiline={true}
          data-html={true}
          html
        />
      </h6>
      <div className="addressLookupInputRow">
        <input
          type="text"
          className="txtBox2 addressLookupInput"
          style={{ width: "400px" }}
          disabled={Boolean(googleError)}
          onChange={(event) => {
            setQuery(event.target.value);
            setLookupMessage("");
          }}
          placeholder="Sök adress eller plats"
          value={query}
        />
        <span
          id="address-map-tooltip-anchor"
          className="addressLookupMapButtonAnchor"
          data-tooltip-content="Välj plats på karta"
        >
          <button
            type="button"
            className="addressLookupMapButton"
            disabled={Boolean(googleError)}
            onClick={() => setIsMapOpen(true)}
            aria-label="Välj plats på karta"
          >
            <FaMapMarkerAlt className="addressLookupMapButtonIcon" />
          </button>
        </span>
        <Tooltip
          anchorId="address-map-tooltip-anchor"
          className="infoCircelTop2"
        />
      </div>

      {query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
        <small className="addressLookupMessage">
          Skriv minst {MIN_QUERY_LENGTH} tecken för att starta sökningen.
        </small>
      )}

      {!googleError && isLoadingPredictions && (
        <small className="addressLookupMessage">Söker adresser...</small>
      )}

      {!googleError && isResolvingSelection && (
        <small className="addressLookupMessage">Hämtar vald adress...</small>
      )}

      {googleError && (
        <small className="addressLookupError">{googleError}</small>
      )}

      {!googleError && lookupMessage && (
        <small className="addressLookupMessage">{lookupMessage}</small>
      )}

      {predictions.length > 0 && (
        <div className="addressLookupResults">
          {predictions.map((prediction) => (
            <button
              key={prediction.placeId}
              type="button"
              className="addressLookupResultButton"
              onClick={() => handlePredictionSelect(prediction)}
            >
              <span className="addressLookupResultMain">
                {prediction.text?.toString?.() || "Okänd plats"}
              </span>
              <span className="addressLookupResultSecondary">Sverige</span>
            </button>
          ))}
        </div>
      )}

      <Modal
        show={isMapOpen}
        onHide={() => setIsMapOpen(false)}
        centered={true}
        dialogClassName="addressLookupModalDialog"
        contentClassName="addressLookupModalContent"
      >
        <Modal.Header closeButton={true} className="addressLookupModalHeader">
          <Modal.Title>Välj plats på karta</Modal.Title>
        </Modal.Header>
        <Modal.Body className="addressLookupModalBody">
          <p className="addressLookupMapIntro">
            Klicka på kartan för att placera en markör. Vi försöker sedan hitta
            en kommun i Sverige utifrån platsen.
          </p>
          <div ref={mapContainerRef} className="addressLookupMapCanvas" />
          {isResolvingMapSelection && (
            <small className="addressLookupMessage">
              Hämtar adress från kartan...
            </small>
          )}
          {mapSelection && (
            <div className="addressLookupMapSelection">
              <strong>Vald plats:</strong>
              <p>{mapSelection.formattedAddress}</p>
              <small className="addressLookupMessage">
                {mapSelection.municipalityMatch
                  ? `Kommunmatch: ${mapSelection.municipalityMatch.municipality}`
                  : "Ingen kommunmatch hittades. Du kan fortfarande välja platsen och sedan sätta Kommun manuellt."}
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="addressLookupModalFooter">
          <button
            type="button"
            className="btn"
            onClick={() => setIsMapOpen(false)}
          >
            Stäng
          </button>
          <button
            type="button"
            className="btn"
            disabled={!mapSelection}
            onClick={handleMapSelectionConfirm}
          >
            Använd plats
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddressLookup;
